import React, { useMemo, useState } from 'react'
import {
  Card,
  Button,
  Space,
  Typography,
  Modal,
  Checkbox,
  Spin,
  message
} from 'antd'
import {
  ChevronDown,
  ChevronRight,
  FileDown,
  Trash2,
  Check,
  RefreshCw,
  ExternalLink
} from 'lucide-react'
import { useFFEStore } from '../../../store/ffeStore'
import { OrderTotals } from '../OrderTotals'
import { CompletedPurchaseOrder } from '../../../types/purchaseOrder'
import { CompletedOrderItemList } from './CompletedOrderItemList'
import { LineItemMaterial } from '../../../types/ffe'
import { useApi } from '../../../services/ApiContext'
import FileSaver from 'file-saver'
import { useCompanyStore } from '../../../store/company/companyStore'
import { Link } from 'react-router-dom'

const { Text } = Typography

interface Props {
  order: CompletedPurchaseOrder
}

export const CompletedOrderCard: React.FC<Props> = ({ order }) => {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [revertItems, setRevertItems] = useState(true)
  const updateLineItem = useFFEStore((state) => state.updateLineItem)
  const setPurchaseOrders = useFFEStore((state) => state.setPurchaseOrders)
  const updateLineItemMaterialStatus = useFFEStore(
    (state) => state.updateLineItemMaterialStatus
  )
  const documentCloudId = useFFEStore((state) => state.document._id)
  const lineItems = useFFEStore((state) => state.document.lineItems)
  const company = useCompanyStore((state) => state.company)
  const patchCompletedPurchaseOrder = useFFEStore(
    (state) => state.patchCompletedPurchaseOrder
  )
  const api = useApi()

  const [loadingExport, setLoadingExport] = useState(false)
  const [loadingXero, setLoadingXero] = useState(false)

  const handleDelete = async () => {
    if (revertItems) {
      // Revert items back to 'specified' status
      order.items.forEach((item) => {
        if (item.type === 'lineItem') {
          updateLineItem(item.id, { status: 'specified' })
        } else {
          // find line item with that material and update status
          const parentItem = lineItems.find((i) =>
            i.materials?.some((m) => m.id === item.id)
          )
          if (parentItem) {
            updateLineItemMaterialStatus(
              parentItem.id,
              item.materialId ?? '',
              'specified'
            )
          }
        }
      })
    }

    // Delete the purchase order
    if (order._id) {
      await api.removePurchaseOrder(order._id)
    }
    loadPurchaseOrders()
    setDeleteModalVisible(false)
  }

  const handleMarkDelivered = () => {
    for (const item of order.items) {
      if (item.type === 'lineItem') {
        updateLineItem(item.id, { status: 'delivered' })
      }
      if (item.type === 'material') {
        // find line item with that material and update status
        const parentItem = lineItems.find((i) =>
          i.materials?.some((m) => m.id === item.id)
        )
        if (parentItem) {
          updateLineItemMaterialStatus(
            parentItem.id,
            item.materialId ?? '',
            'delivered'
          )
        }
      }
    }
  }

  const loadPurchaseOrders = async () => {
    if (documentCloudId) {
      const purchaseOrders = await api.loadPurchaseOrders(documentCloudId)
      setPurchaseOrders(purchaseOrders)
      return
    }
  }

  const counts = useMemo(() => {
    // return total count and total ordered count
    const totalCount = order.items.length
    const purchaseOrderItems = order.items.map((item) =>
      lineItems.find((li) => li.id === item.id)
    )
    const purchaseOrderMaterials = []
    for (const material of order.items.filter(
      (item) => item.type === 'material'
    )) {
      let foundMaterial: LineItemMaterial | null = null
      for (const lineItem of lineItems) {
        if (!lineItem.materials) continue
        const check = lineItem.materials.find((liMa) => liMa.id === material.id)
        if (check) {
          foundMaterial = check
          break
        }
      }
      if (foundMaterial) {
        purchaseOrderMaterials.push(foundMaterial)
      }
    }
    const deliveredCount =
      purchaseOrderItems.filter((item) => item?.status === 'delivered').length +
      purchaseOrderMaterials.filter((item) => item?.status === 'delivered')
        .length
    return { totalCount, deliveredCount }
  }, [order.items, lineItems])

  const downloadPDF = async () => {
    setLoadingExport(true)
    const response = await api.downloadPurchaseOrderPDF(company, order)

    FileSaver.saveAs(
      response,
      `PO-${order.poNumber ? order.poNumber + '-' : ''}${new Date(order.date).toLocaleDateString()}.pdf`
    )
    setLoadingExport(false)
  }

  const sendToXero = async () => {
    if (!order._id) return
    setLoadingXero(true)
    try {
      const response = await api.sendPurchaseOrderToXero(order._id)
      patchCompletedPurchaseOrder(
        order.id,
        {
          xeroUrl: response.data.link,
        }
      )
      message.success(
        <span>
          PO Sent to Xero!{' '}
          <a href={response.data.link} target="_blank">
            Click here to open
          </a>
        </span>
      )
    } catch (error) {
      message.error('Failed to send purchase order to Xero')
      setLoadingXero(false)
      throw error
    }
    setLoadingXero(false)
  }

  return (
    <Card className="bg-white mb-4" styles={{ body: { padding: 0 } }}>
      {/* Header */}
      <div
        className="cursor-pointer flex items-center gap-4 p-4 bg-gray-50 rounded-lg"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <Button
          type="text"
          icon={
            isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )
          }
        />

        <div className="flex-1">
          <Text strong>{order.supplier.name}</Text>
          {order.poNumber && (
            <Text type="secondary" className="ml-2">
              PO #{order.poNumber}
            </Text>
          )}
        </div>
        {counts.deliveredCount < counts.totalCount && (
          <div className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-sm">
            {counts.deliveredCount} / {counts.totalCount} items delivered
          </div>
        )}
        {counts.deliveredCount >= counts.totalCount && (
          <div className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm">
            {counts.deliveredCount} / {counts.totalCount} items delivered
          </div>
        )}
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="border-t p-6">
          <CompletedOrderItemList items={order.items} />
          <OrderTotals
            subtotal={order.subtotal || 0}
            tax={order.tax || 0}
            total={order.total || 0}
          />

          <div className="mt-4 flex justify-between">
            <Space>
              <Button
                danger
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => setDeleteModalVisible(true)}
              >
                Delete
              </Button>
              <Spin spinning={loadingExport}>
                <Button
                  icon={<FileDown className="h-4 w-4" />}
                  onClick={downloadPDF}
                >
                  Export PDF
                </Button>
              </Spin>
              <Spin spinning={loadingXero}>
                {company.isXeroConnected && !company.xeroNeedsReconnect ? (
                  <>
                    {order.xeroUrl ? (
                      <>
                        <Button
                          icon={<ExternalLink className="h-4 w-4" />}
                          onClick={() => window.open(order.xeroUrl, '_blank')}
                        >
                          View in Xero
                        </Button>
                      </>
                    ) : (
                      <Button
                        icon={<RefreshCw className="h-4 w-4" />}
                        onClick={sendToXero}
                      >
                        Send to Xero
                      </Button>
                    )}
                  </>
                ) : company.isXeroConnected && company.xeroNeedsReconnect ? (
                  <Link to="/app/settings/integrations/xero">
                    <Button
                      icon={<RefreshCw className="h-4 w-4" />}
                      className="!text-orange-500 !border-orange-500"
                    >
                      Reconnect Xero
                    </Button>
                  </Link>
                ) : (
                  <Link to="/app/settings/integrations/xero">
                    <Button icon={<RefreshCw className="h-4 w-4" />}>
                      Send to Xero
                    </Button>
                  </Link>
                )}
              </Spin>
            </Space>

            <Button
              type="primary"
              icon={<Check className="h-4 w-4" />}
              onClick={handleMarkDelivered}
            >
              Mark as Delivered
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Purchase Order"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this purchase order?</p>
        <Checkbox
          checked={revertItems}
          onChange={(e) => setRevertItems(e.target.checked)}
          className="mt-4"
        >
          Move items back to "Specified" status
        </Checkbox>
      </Modal>
    </Card>
  )
}
